import { Badge } from '@/components/ui/badge';
import { ChannelBadge, type BookingChannel } from '@/components/ui/channel-badge';
import {
  BOOKING_STATUS_TONE,
  OPERATIONAL_STATUS_TONE,
  REVISION_STATUS_TONE,
  SCHEDULED_STATUS_TONE,
  type BookingStatus,
  type OperationalStatus,
  type RevisionStatus,
  type ScheduledStatus,
} from '@/lib/status-tone';

// Thin DS-Badge wrappers over the status-tone SoT. Each renders a dotted kit
// Badge in the mapped variant with an accessible "<Kind>: <label>" name.

export function BookingStatusBadge({ status }: { status: BookingStatus }) {
  const t = BOOKING_STATUS_TONE[status];
  return (
    <Badge variant={t.variant} dot aria-label={`Status: ${t.label}`}>
      {t.label}
    </Badge>
  );
}

export function OperationalStatusBadge({ status }: { status: OperationalStatus }) {
  const t = OPERATIONAL_STATUS_TONE[status];
  return (
    <Badge variant={t.variant} dot aria-label={`Stay: ${t.label}`}>
      {t.label}
    </Badge>
  );
}

export function RevisionStatusBadge({ status }: { status: RevisionStatus }) {
  const t = REVISION_STATUS_TONE[status];
  return (
    <Badge variant={t.variant} dot aria-label={`Revision: ${t.label}`}>
      {t.label}
    </Badge>
  );
}

export function ScheduledStatusBadge({ status }: { status: ScheduledStatus }) {
  const t = SCHEDULED_STATUS_TONE[status];
  return (
    <Badge variant={t.variant} dot aria-label={`Message status: ${t.label}`}>
      {t.label}
    </Badge>
  );
}

// Booking source/channel identity (NOT a status). OTA channels with a kit
// ChannelBadge (brand colors) use it; expedia/manual fall back to a neutral
// Badge. Shared by bookings.index + bookings.$id.
const SOURCE_CHANNEL: Partial<Record<string, BookingChannel>> = {
  direct: 'direct',
  booking_com: 'booking',
  airbnb: 'airbnb',
};

export const SOURCE_LABELS: Record<string, string> = {
  direct: 'Direct',
  booking_com: 'Booking.com',
  airbnb: 'Airbnb',
  expedia: 'Expedia',
  manual: 'Manual',
};

export function BookingSourceBadge({ source }: { source: string }) {
  const channel = SOURCE_CHANNEL[source];
  if (channel) return <ChannelBadge channel={channel} />;
  return (
    <Badge variant="neutral" dot>
      {SOURCE_LABELS[source] ?? source}
    </Badge>
  );
}
